import {
  CustomFieldDefinition,
  CustomFieldGroupDefinition,
  MetastructureField,
  MetastructureFieldType,
  ResourceType
} from '__types__';
import { ResourceTypeFieldAssignment } from 'v5/api/resource-types/resource-type-bindings.api';
import { mapAssignmentsToMetastructureFields } from 'v5/services/internal/ResourceTypeBindingsService/mapAssignmentsToMetastructureFields';
import {
  formatCustomFieldDataTypeLabel,
  getCoreFieldDataTypeLabel,
  getCoreFieldLabel,
  getFieldGroupDataTypeLabel
} from 'v5/services/internal/MetastructureMappingService/coreFieldLabels';
import { ResourceTypeAdmin, ResourceTypeFieldView } from './model';

export { mapAssignmentsToMetastructureFields };

/** Lean persistence assignments from metastructure (no display labels). */
export function mapMetastructureFieldsToAssignments(
  fields: MetastructureField[] | undefined
): ResourceTypeFieldAssignment[] {
  if (!fields?.length) return [];

  return fields
    .map((field, index): ResourceTypeFieldAssignment | null => {
      if (field.type === MetastructureFieldType.Core) {
        return {
          id: `core:${field.name}`,
          source: 'core',
          coreFieldKey: field.name,
          required: field.required ?? false,
          locked: field.locked ?? false,
          active: field.active ?? true,
          order: index
        };
      }
      if (field.type === MetastructureFieldType.Custom) {
        return {
          id: `custom:${field.custom_field_definition_id}`,
          source: 'custom',
          customFieldDefinitionId: field.custom_field_definition_id,
          required: field.required ?? false,
          locked: field.locked ?? false,
          active: field.active ?? true,
          order: index
        };
      }
      return {
        id: `group:${field.custom_fieldgroup_definition_id}`,
        source: 'group',
        customFieldGroupDefinitionId: field.custom_fieldgroup_definition_id,
        required: field.required ?? false,
        locked: field.locked ?? false,
        active: field.active ?? true,
        order: index
      };
    })
    .filter((field): field is ResourceTypeFieldAssignment => field != null);
}

/**
 * Resolve display name / type label / required / filterable from definitions +
 * core labels. For custom fields, `required` and `filterable` come from the
 * custom field definition (same source forms and the CF config dialog use) —
 * not from the metastructure assignment, which often stays at the default
 * `false` when fields are assigned.
 */
export function resolveFieldAssignments(
  assignments: ResourceTypeFieldAssignment[],
  definitionsById: Record<number, CustomFieldDefinition>,
  groupDefinitionsById: Record<number, CustomFieldGroupDefinition> = {}
): ResourceTypeFieldView[] {
  return assignments.map(assignment => {
    if (assignment.source === 'core') {
      return {
        ...assignment,
        name: getCoreFieldLabel(assignment.coreFieldKey),
        dataTypeLabel: getCoreFieldDataTypeLabel(),
        filterable: true
      };
    }
    if (assignment.source === 'group') {
      const group =
        groupDefinitionsById[assignment.customFieldGroupDefinitionId];
      return {
        ...assignment,
        name:
          group?.name ??
          `Field group ${assignment.customFieldGroupDefinitionId}`,
        dataTypeLabel: getFieldGroupDataTypeLabel(group?.is_array ?? false),
        // Members carry their own `required` and filters.
        filterable: false
      };
    }
    const def = definitionsById[assignment.customFieldDefinitionId];
    return {
      ...assignment,
      name: def?.name ?? `Custom field ${assignment.customFieldDefinitionId}`,
      dataTypeLabel: formatCustomFieldDataTypeLabel(def?.data_type ?? 'CUSTOM'),
      // Prefer definition; fall back to assignment when the catalog row is missing.
      required: def?.required ?? assignment.required,
      filterable: def?.filterable ?? false
    };
  });
}

/** Drop view-only props before writing bindings / metastructure. */
export function toFieldAssignment(
  field: ResourceTypeFieldAssignment | ResourceTypeFieldView
): ResourceTypeFieldAssignment {
  const shared = {
    id: field.id,
    required: field.required,
    locked: field.locked,
    active: field.active,
    order: field.order
  };
  switch (field.source) {
    case 'core':
      return { ...shared, source: 'core', coreFieldKey: field.coreFieldKey };
    case 'custom':
      return {
        ...shared,
        source: 'custom',
        customFieldDefinitionId: field.customFieldDefinitionId
      };
    case 'group':
      return {
        ...shared,
        source: 'group',
        customFieldGroupDefinitionId: field.customFieldGroupDefinitionId
      };
  }
}

export function mapResourceTypeToAdmin(
  resourceType: ResourceType,
  definitionsById: Record<number, CustomFieldDefinition> = {},
  groupDefinitionsById: Record<number, CustomFieldGroupDefinition> = {}
): ResourceTypeAdmin {
  return {
    id: resourceType.id,
    name: resourceType.name ?? '',
    icon: resourceType.icon ?? 'person',
    mechanism: resourceType.model ?? 'INDIVIDUAL',
    archived: Boolean(resourceType.archived),
    order: resourceType.order ?? 0,
    globalFields: resolveFieldAssignments(
      mapMetastructureFieldsToAssignments(resourceType.metastructure?.fields),
      definitionsById,
      groupDefinitionsById
    )
  };
}
